import { useEffect, useState } from 'react';
import { LlaveDelAyudante } from '@renderer/components/config/LlaveDelAyudante';
import { useSearchParams } from 'react-router-dom';
import { Plus, Save, Trash2, Edit2, Upload, Printer, MapPin } from 'lucide-react';
// Sprint 111 — El mismo mapa del flujo de domicilio, reutilizado para ubicar el negocio.
import { MapaPicker } from '@renderer/components/pos/MapaPicker';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { PALETA_BRANDING } from '@shared/constants';
import { TicketPreview } from '@renderer/components/TicketPreview';
import { aplicarColorPrimario } from '@renderer/utils/colors';
import { FormMesa } from '@renderer/components/forms/FormMesa';
import { CambiarPinForm } from '@renderer/components/forms/CambiarPinForm';
import { DosFactoresForm } from '@renderer/components/forms/DosFactoresForm';
import { AvisoEnCaminoForm } from '@renderer/components/settings/AvisoEnCaminoForm';
import { PrivacidadForm } from '@renderer/components/settings/PrivacidadForm';
import { PlantillaWaForm } from '@renderer/components/settings/PlantillaWaForm';
// Sprint 76 — Cuánto se espera el pago de un domicilio y con qué datos se le cobra.
import { DomicilioForm } from '@renderer/components/settings/DomicilioForm';
import { MenuPublicoForm } from '@renderer/components/settings/MenuPublicoForm';
import { BackupsForm } from '@renderer/components/settings/BackupsForm';
import { SmtpForm } from '@renderer/components/settings/SmtpForm';
import { ImpuestosForm } from '@renderer/components/settings/ImpuestosForm';
import { AparienciaForm } from '@renderer/components/settings/AparienciaForm';
import { AgenteImpresionSection } from '@renderer/components/settings/AgenteImpresionSection';
import { UnidadesMedidaSection } from '@renderer/components/settings/UnidadesMedidaSection';
import { NotasRapidasForm } from '@renderer/components/settings/NotasRapidasForm';
import { PermisosSection } from '@renderer/components/settings/PermisosSection';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirDocumento } from '@renderer/lib/impresionNavegador';
import { logoTicketUrl, logoUrl } from '@renderer/utils/logoUrl';
import type { MesaVista } from '@shared/types';

/**
 * Sprint 118 — Los grupos, por cada cuánto se tocan.
 *
 * Mismo criterio que el menú lateral (Sprint 114): la frecuencia de uso, no el tema. Si mañana se
 * agrega un ajuste, la pregunta para colocarlo no es "¿de qué trata?" sino **"¿cada cuánto se
 * abre?"** — y si la respuesta es "una vez, al instalar", ya tiene su sitio.
 */
const GRUPOS_CONFIG: {
  titulo: string;
  items: { id: Tab; etiqueta: string; soloDueno?: boolean }[];
}[] = [
  {
    titulo: 'Mi negocio',
    items: [
      { id: 'datos', etiqueta: 'Datos del negocio' },
      { id: 'branding', etiqueta: 'Logo y colores' },
      { id: 'mesas', etiqueta: 'Mesas' },
    ],
  },
  {
    titulo: 'Cómo vendo',
    items: [
      // Aquí viven las tarifas de envío y los tiempos de espera: se ajustan con el negocio.
      { id: 'whatsapp', etiqueta: 'WhatsApp y domicilio', soloDueno: true },
      { id: 'impresora', etiqueta: 'Impresora' },
      // Sprint 63 (Fase 4) — El dueño preguntó dónde se daban de alta: aquí.
      { id: 'unidades', etiqueta: 'Unidades de medida' },
      /*
        Sprint 138 — Va en "Cómo vendo" y no en "Se configura una vez" a propósito: el negocio va
        a volver aquí cada vez que aparezca una forma nueva de pedir un corte, y eso pasa seguido.
      */
      { id: 'notas', etiqueta: 'Cómo se pide la carne', soloDueno: true },
    ],
  },
  {
    titulo: 'Se configura una vez',
    items: [
      { id: 'apariencia', etiqueta: 'Apariencia' },
      // Sprint 64 — "se vería mejor si en CONFIGURACIÓN tuviera su UI/UX".
      { id: 'permisos', etiqueta: 'Permisos', soloDueno: true },
      { id: 'seguridad', etiqueta: 'Seguridad', soloDueno: true },
      { id: 'email', etiqueta: 'Email', soloDueno: true },
      { id: 'respaldos', etiqueta: 'Respaldos', soloDueno: true },
      // Sprint 167 — El aviso de privacidad del enlace y borrar a un cliente que lo pida.
      { id: 'privacidad', etiqueta: 'Privacidad de clientes', soloDueno: true },
    ],
  },
];

type Tab =
  | 'datos'
  | 'branding'
  | 'apariencia'
  | 'impresora'
  | 'mesas'
  // Sprint 63 (Fase 4) — Catalogo de unidades de medida.
  | 'unidades'
  // Sprint 138 — Términos, cortes y preparación: el vocabulario de la cocina.
  | 'notas'
  // Sprint 64 — Quien puede hacer que.
  | 'permisos'
  | 'whatsapp'
  | 'respaldos'
  | 'email'
  | 'seguridad'
  | 'privacidad';

export function SettingsPage() {
  const negocio = useNegocioStore((s) => s.negocio);
  const setNegocio = useNegocioStore((s) => s.setNegocio);
  const sesion = useSessionStore((s) => s.sesion);
  const [params] = useSearchParams();
  const tabQuery = params.get('tab') as Tab | null;
  const esDueno = sesion?.rol === 'dueño';
  const tabsValidas: Tab[] = [
    'datos',
    'branding',
    'apariencia',
    'mesas',
    'unidades',
    'impresora',
  ];
  if (esDueno) tabsValidas.push('whatsapp', 'respaldos', 'email', 'seguridad', 'permisos', 'notas', 'privacidad');
  const tabInicial: Tab = tabQuery && tabsValidas.includes(tabQuery) ? tabQuery : 'datos';
  const [tab, setTab] = useState<Tab>(tabInicial);

  if (!negocio) return <div>Cargando...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Configuración</h1>
        <p className="text-sm text-slate-500">Personaliza tu negocio.</p>
      </div>

      {/*
        Sprint 118 — Once pestañas en dos filas, agrupadas por CADA CUÁNTO se tocan.

        Reporte del dueño: *"la sección de configuración creo que tiene demasiadas pestañas"*. Eran
        once, en dos filas —"Permisos" caía sola en la segunda, que ya es la señal de que no
        caben— repartiendo **47 campos** sin ninguna jerarquía. A quien apenas está aprendiendo el
        sistema, once pestañas no le dicen "aquí hay opciones": le dicen *"hay once decisiones que
        deberías estar tomando"*, y la respuesta sensata es no entrar.

        Los grupos son los mismos tres del menú lateral, por la misma razón: **no agrupan por tema
        sino por frecuencia**, que es lo que el operador vive. Lo que se toca cuando cambia el
        negocio, lo que ajusta el día a día, y lo que se configuró una vez al instalar y no se
        vuelve a abrir.

        **Nada se esconde y nada se quita**: los once destinos siguen a un toque. Cambia que ahora
        cada uno dice *cuándo* le toca. Y el tercer grupo se llama "Se configura una vez" y no
        "Avanzado": esa palabra es justo la que hizo que el Corte de Caja fuera invisible durante
        meses — a alguien de 3/10 le dice "esto no es para ti".
      */}
      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        <nav className="space-y-3 md:border-r md:border-slate-200 md:pr-3">
          {GRUPOS_CONFIG.map((grupo) => {
            const visibles = grupo.items.filter((i) => !i.soloDueno || esDueno);
            if (visibles.length === 0) return null;
            return (
              <div key={grupo.titulo}>
                <div className="px-2 pb-1 text-xs font-bold uppercase tracking-wider text-slate-600">
                  {grupo.titulo}
                </div>
                <ul className="space-y-0.5">
                  {visibles.map((i) => (
                    <li key={i.id}>
                      <button
                        type="button"
                        onClick={() => setTab(i.id)}
                        className={`flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-sm transition ${
                          tab === i.id
                            ? 'bg-primary font-semibold text-primary-fg'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {i.etiqueta}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="min-w-0">

      {tab === 'datos' && (
        <div className="space-y-4">
          <DatosNegocio negocio={negocio} setNegocio={setNegocio} />
          {/*
            Sprint 73 — Los impuestos se configuraban SÓLO en el asistente de instalación y después
            no había forma de cambiarlos. Por eso quedaron en 16% sumando encima de precios que ya lo
            incluían: cada ticket cobraba 16% de más. Un dato que se pregunta una vez y no se puede
            corregir es un dato que va a quedar mal.
          */}
          {esDueno && <ImpuestosForm />}
        </div>
      )}
      {tab === 'branding' && <Branding negocio={negocio} setNegocio={setNegocio} />}
      {tab === 'apariencia' && <AparienciaForm />}
      {tab === 'mesas' && <MesasAdmin />}
      {tab === 'unidades' && <UnidadesMedidaSection />}
      {/* Sprint 138 — Los botones de "cómo lo quiere el cliente". Sólo el dueño los cambia. */}
      {tab === 'notas' && esDueno && <NotasRapidasForm />}
      {tab === 'impresora' && <ImpresoraPlaceholder />}
      {tab === 'whatsapp' && esDueno && (
        <div className="space-y-6">
          {/* Sprint 76 — Los ajustes del flujo a domicilio viven junto a la plantilla de
              WhatsApp porque son el mismo flujo: todo el pedido ocurre en ese chat. */}
          <DomicilioForm />
          {/* Sprint 163 — El aviso "va en camino" al celular del cliente. */}
          <div className="border-t border-slate-200 pt-6">
            <AvisoEnCaminoForm />
          </div>
          {/* Sprint 92 — El enlace del menú vive aquí por lo mismo: es el mismo chat. */}
          <div className="border-t border-slate-200 pt-6">
            <MenuPublicoForm />
          </div>
          <div className="border-t border-slate-200 pt-6">
            <PlantillaWaForm />
          </div>
        </div>
      )}
      {tab === 'respaldos' && esDueno && <BackupsForm />}
      {tab === 'email' && esDueno && <SmtpForm />}
      {tab === 'permisos' && esDueno && <PermisosSection />}
      {tab === 'privacidad' && esDueno && <PrivacidadForm />}
      {tab === 'seguridad' && esDueno && (
        <div className="flex flex-wrap gap-4">
          <CambiarPinForm />
          <DosFactoresForm />
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint 7.2: ABM de mesas movido aquí desde MesasPage.
 * Why: la página /mesas es operativa (estado vivo de pedidos), el ABM es admin (raro, dueño-only).
 */
function MesasAdmin() {
  const [mesas, setMesas] = useState<MesaVista[]>([]);
  const [editando, setEditando] = useState<MesaVista | null>(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.mesas.listar();
    if (res.ok) setMesas(res.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const onEliminar = async (m: MesaVista) => {
    const ok = await window.confirmar({ titulo: 'Eliminar mesa', mensaje: `¿Eliminar mesa #${m.numero}?`, textoConfirmar: 'Eliminar', variant: 'destructive' });
    if (!ok) return;
    const res = await window.api.mesas.eliminar(m.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'No se pudo eliminar', mensaje: traducirErrorBackend(res.error, res.codigo), variant: 'warn' });
  };

  return (
    <div className="card max-w-2xl space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Catálogo de mesas</h2>
          <p className="text-xs text-slate-500">
            Alta, edición y eliminación. El estado se actualiza automáticamente con los pedidos.
          </p>
        </div>
        <button
          onClick={() => {
            setEditando(null);
            setDrawerAbierto(true);
          }}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" />
          Nueva mesa
        </button>
      </div>

      {cargando ? (
        <div className="text-center text-slate-400 py-6">Cargando...</div>
      ) : mesas.length === 0 ? (
        <div className="text-center text-slate-400 py-6">
          <p>No hay mesas registradas.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Capacidad</th>
                <th className="px-3 py-2">Observaciones</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mesas.slice().sort((a, b) => a.numero - b.numero).map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono font-bold">#{m.numero}</td>
                  <td className="px-3 py-2">{m.capacidad}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{m.observaciones ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditando(m);
                          setDrawerAbierto(true);
                        }}
                        className="btn-ghost p-1"
                        aria-label="Editar"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onEliminar(m)}
                        className="btn-ghost p-1 text-red-600"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormMesa
        abierto={drawerAbierto}
        mesa={editando}
        onClose={() => setDrawerAbierto(false)}
        onSaved={() => {
          setDrawerAbierto(false);
          cargar();
        }}
      />
    </div>
  );
}

/*
  Sprint 118 — `TabButton` se retira con la barra de once pestañas que servía.

  Se borra en vez de dejarlo "por si acaso": un componente sin usos es el que alguien reutiliza
  dentro de seis meses reintroduciendo el diseño que hoy estamos quitando.
*/

function DatosNegocio({
  negocio,
  setNegocio,
}: {
  negocio: NonNullable<ReturnType<typeof useNegocioStore.getState>['negocio']>;
  setNegocio: (n: any) => void;
}) {
  const [form, setForm] = useState({
    nombre: negocio.nombre,
    rfc: negocio.rfc ?? '',
    direccion: negocio.direccion ?? '',
    telefono: negocio.telefono ?? '',
    email: negocio.email ?? '',
    mensajePieTicket: negocio.mensajePieTicket ?? '',
    leyendaFiscal: negocio.leyendaFiscal ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  /**
   * Sprint 111 — Dónde está el negocio, en el mapa.
   *
   * La dirección de texto ya estaba ("frente a farmacias Guadalajara"), pero es una referencia
   * humana: un geocodificador puede errarle por cuadras, y con el origen mal **todas** las
   * distancias de envío salen mal, siempre y en silencio. Se ratifica arrastrando el pin.
   */
  const [mapaAbierto, setMapaAbierto] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    negocio.latitud != null && negocio.longitud != null
      ? { lat: negocio.latitud, lng: negocio.longitud }
      : null,
  );

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    const res = await window.api.negocio.actualizar({
      ...form,
      latitud: coords?.lat ?? null,
      longitud: coords?.lng ?? null,
    });
    setGuardando(false);
    if (res.ok) {
      setNegocio(res.data);
      setMensaje('Cambios guardados');
      setTimeout(() => setMensaje(null), 3000);
    } else {
      setMensaje(`Error: ${traducirErrorBackend(res.error, res.codigo)}`);
    }
  };

  return (
    <div className="card max-w-xl space-y-4">
      <Field label="Nombre del negocio">
        <input className="input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
      </Field>
      <Field label="RFC">
        <input className="input uppercase" value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} />
      </Field>
      <Field label="Dirección">
        <input className="input" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
      </Field>

      {/*
        Sprint 111 — El punto exacto del negocio, ratificado a mano.

        No se resuelve desde el texto de la dirección a propósito: *"Calle Simón Sarlat y la vía
        #407, frente a farmacias Guadalajara"* es una referencia humana que un geocodificador puede
        situar dos cuadras más allá. Ese error no se detectaría nunca —una tarifa sugerida
        equivocada se ve razonable— y afectaría a **todos** los envíos. Arrastrar el pin es una
        ratificación hecha por alguien que conoce el lugar.
      */}
      <Field label="Dónde está el negocio (para calcular envíos)">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setMapaAbierto(true)}
            className="btn-ghost min-h-[44px] border border-slate-300"
          >
            <MapPin className="h-4 w-4" />
            {coords ? 'Cambiar la ubicación' : 'Ubicar en el mapa'}
          </button>
          {coords ? (
            <span className="text-xs text-emerald-700">
              ✓ Ubicado ({coords.lat.toFixed(5)}, {coords.lng.toFixed(5)})
            </span>
          ) : (
            <span className="text-xs text-amber-700">
              Sin ubicar: no se puede sugerir la tarifa de envío por distancia.
            </span>
          )}
        </div>
      </Field>

      {mapaAbierto && (
        <MapaPicker
          latInicial={coords?.lat}
          lngInicial={coords?.lng}
          direccionInicial={form.direccion || undefined}
          onConfirmar={(lat, lng) => {
            setCoords({ lat, lng });
            setMapaAbierto(false);
            setMensaje('Ubicación puesta. Falta darle a Guardar.');
          }}
          onCerrar={() => setMapaAbierto(false)}
        />
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Teléfono">
          <input className="input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
        </Field>
        <Field label="Email">
          <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
      </div>
      <Field label="Mensaje al pie del ticket">
        <textarea
          className="input"
          rows={2}
          maxLength={200}
          value={form.mensajePieTicket}
          onChange={(e) => setForm({ ...form, mensajePieTicket: e.target.value })}
        />
      </Field>

      {/*
        Sprint 130 — Cómo pedir factura.

        La dueña factura y lo gestiona por WhatsApp. Sin esta línea en el ticket, quien necesita
        factura se entera cuando ya se fue.

        El texto es **suyo**, no del sistema: los plazos y las condiciones los fija su contador.
        Aquí sólo se ofrece un punto de partida, y se puede dejar vacío — un negocio que no
        factura no debe llevar letra que nadie lee.
      */}
      <Field label="¿Cómo se pide una factura? (sale en el ticket)">
        <textarea
          className="input"
          rows={3}
          maxLength={240}
          placeholder={
            'Este ticket no es comprobante fiscal.\n' +
            '¿Necesitas factura? Mándanos este folio por WhatsApp.'
          }
          value={form.leyendaFiscal}
          onChange={(e) => setForm({ ...form, leyendaFiscal: e.target.value })}
        />
        <p className="mt-1 text-xs text-slate-600">
          Déjalo vacío si no facturas. El folio ya sale impreso arriba del ticket, así que basta
          con pedirle al cliente que lo mencione.
        </p>
      </Field>

      {mensaje && (
        <div className={`rounded-lg px-4 py-2 text-sm ${
          mensaje.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>{mensaje}</div>
      )}

      {/* Sprint 136 — Que el negocio compre su propio crédito del ayudante. */}
      <LlaveDelAyudante />

      <button onClick={guardar} className="btn-primary" disabled={guardando}>
        <Save className="h-4 w-4" />
        Guardar cambios
      </button>
    </div>
  );
}

function Branding({
  negocio,
  setNegocio,
}: {
  negocio: NonNullable<ReturnType<typeof useNegocioStore.getState>['negocio']>;
  setNegocio: (n: any) => void;
}) {
  const [color, setColor] = useState(negocio.colorPrimario);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const handleColor = (hex: string) => {
    setColor(hex);
    aplicarColorPrimario(hex);
  };

  const guardarColor = async () => {
    setGuardando(true);
    const res = await window.api.negocio.actualizar({ colorPrimario: color });
    setGuardando(false);
    if (res.ok) {
      setNegocio(res.data);
      setMensaje('Color actualizado');
      setTimeout(() => setMensaje(null), 2000);
    } else {
      setMensaje(`Error: ${traducirErrorBackend(res.error, res.codigo)}`);
    }
  };

  const handleLogo = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setMensaje('Imagen muy grande (máx 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setGuardando(true);
      const res = await window.api.negocio.guardarLogo(reader.result as string);
      if (res.ok) {
        const refreshed = await window.api.negocio.obtener();
        if (refreshed.ok) setNegocio(refreshed.data);
        setMensaje('Logo actualizado');
      } else {
        setMensaje(`Error: ${traducirErrorBackend(res.error, res.codigo)}`);
      }
      setGuardando(false);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Sprint 107 — La imagen que sale por la impresora térmica.
   *
   * Petición del negocio: *"la impresora térmica no imprime a color y necesita una imagen sólo con
   * los bordes del logo"*. Convertir el logo a grises no siempre alcanza —uno con relleno oscuro
   * sale como una mancha—, así que aquí se sube **otro dibujo** y el logo de pantalla no se toca.
   */
  const handleLogoTicket = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setMensaje('Imagen muy grande (máx 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setGuardando(true);
      const res = await window.api.negocio.guardarLogoTicket(reader.result as string);
      if (res.ok) {
        const refreshed = await window.api.negocio.obtener();
        if (refreshed.ok) setNegocio(refreshed.data);
        setMensaje('Logo del ticket actualizado');
      } else {
        setMensaje(`Error: ${traducirErrorBackend(res.error, res.codigo)}`);
      }
      setGuardando(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 max-w-3xl">
      <div className="card space-y-4">
        <div>
          <div className="label">Color primario</div>
          <div className="flex flex-wrap gap-2">
            {PALETA_BRANDING.map((c) => (
              <button
                key={c.hex}
                type="button"
                onClick={() => handleColor(c.hex)}
                className={`h-9 w-9 rounded-full border-2 ${
                  color === c.hex ? 'border-slate-900 scale-110' : 'border-white shadow'
                }`}
                style={{ backgroundColor: c.hex }}
                aria-label={c.label}
              />
            ))}
            <input
              type="color"
              value={color}
              onChange={(e) => handleColor(e.target.value)}
              className="h-9 w-9 rounded-full border-2 border-white shadow cursor-pointer"
            />
          </div>
          <button onClick={guardarColor} className="btn-primary mt-3" disabled={guardando}>
            Guardar color
          </button>
        </div>

        <div>
          <div className="label">Logo</div>
          <div className="flex items-center gap-3">
            <div className="flex h-20 w-20 items-center justify-center rounded border border-slate-200 bg-slate-50">
              {negocio.logoPath ? (
                <img src={logoUrl(negocio.fechaModificacion ?? negocio.id)} alt="Logo" className="h-full w-full object-contain p-2" />
              ) : (
                <span className="text-xs text-slate-400">Sin logo</span>
              )}
            </div>
            <label className="btn-secondary cursor-pointer">
              <Upload className="h-4 w-4" />
              <span>Cambiar logo</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleLogo(e.target.files[0])}
              />
            </label>
          </div>
        </div>

        {/*
          Sprint 107 — La segunda imagen: la del ticket.

          Va debajo del logo y con su explicación, porque es un concepto que nadie adivina: por qué
          el mismo negocio necesita **dos** logos. La respuesta es la impresora, y por eso se dice
          con esas palabras y no con "versión monocromática".

          Si nunca se sube ninguna, el ticket usa el logo de pantalla convertido a grises, que es lo
          que se venía haciendo. Esto sólo abre la puerta a hacerlo mejor.
        */}
        <div>
          <div className="label">Logo para el ticket impreso</div>
          <div className="flex items-center gap-3">
            <div className="flex h-20 w-20 items-center justify-center rounded border border-slate-200 bg-white">
              {negocio.logoTicketPath ? (
                <img
                  src={logoTicketUrl(negocio.fechaModificacion ?? negocio.id)}
                  alt="Logo del ticket"
                  className="h-full w-full object-contain p-2"
                />
              ) : (
                <span className="text-xs text-slate-400">Sin logo</span>
              )}
            </div>
            <label className="btn-secondary cursor-pointer">
              <Upload className="h-4 w-4" />
              <span>Cambiar el del ticket</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleLogoTicket(e.target.files[0])}
              />
            </label>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            La impresora térmica <b>no imprime color</b>: los rellenos oscuros salen como una
            mancha. Si tu logo se ve mal en el ticket, sube aquí una versión <b>de contornos</b>,
            sin fondo ni relleno. Si no subes ninguna, se usa tu logo pasado a blanco y negro.
          </p>
        </div>

        {mensaje && (
          <div className={`rounded-lg px-4 py-2 text-sm ${
            mensaje.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>{mensaje}</div>
        )}
      </div>

      <div>
        <div className="label text-center">Vista previa</div>
        <TicketPreview
          nombre={negocio.nombre}
          rfc={negocio.rfc ?? undefined}
          direccion={negocio.direccion ?? undefined}
          mensajePieTicket={negocio.mensajePieTicket ?? undefined}
          logoSrc={negocio.logoPath ? logoUrl(negocio.fechaModificacion ?? negocio.id) : undefined}
        />
      </div>
    </div>
  );
}

function ImpresoraPlaceholder() {
  // Sprint 56 — en navegador no existe "la impresora del servidor": el ticket
  // lo manda esta computadora. La pestaña cambia por completo.
  return esWeb() ? <ConfigImpresoraWebSection /> : <ConfigImpresoraSection />;
}

function ConfigImpresoraSection() {
  const [impresoras, setImpresoras] = useState<{ nombre: string; nombreMostrar: string; esPredeterminada: boolean }[]>([]);
  const [caja, setCaja] = useState('');
  const [cocina, setCocina] = useState('');
  const [cocinaUsarCaja, setCocinaUsarCaja] = useState(false);
  const [ancho, setAncho] = useState('80');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ texto: string; tipo: 'ok' | 'err' } | null>(null);

  useEffect(() => {
    (async () => {
      const [impRes, cajaRes, cocinaRes, anchoRes, usarCajaRes] = await Promise.all([
        window.api.impresoras.listar(),
        window.api.config.obtener('impresora_nombre'),
        window.api.config.obtener('impresora_cocina_nombre'),
        window.api.config.obtener('impresora_ancho_mm'),
        window.api.config.obtener('impresora_cocina_usar_caja'),
      ]);
      if (impRes.ok && impRes.data) setImpresoras(impRes.data);
      if (cajaRes.ok && cajaRes.data) setCaja(cajaRes.data);
      if (cocinaRes.ok && cocinaRes.data) setCocina(cocinaRes.data);
      if (anchoRes.ok && anchoRes.data) setAncho(anchoRes.data);
      if (usarCajaRes.ok) setCocinaUsarCaja(usarCajaRes.data === 'true');
      setCargando(false);
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      await Promise.all([
        window.api.config.actualizar('impresora_nombre', caja.trim()),
        window.api.config.actualizar('impresora_cocina_nombre', cocina.trim()),
        window.api.config.actualizar('impresora_ancho_mm', ancho),
        window.api.config.actualizar('impresora_cocina_usar_caja', cocinaUsarCaja ? 'true' : 'false'),
      ]);
      setMensaje({ texto: 'Configuración guardada.', tipo: 'ok' });
    } catch {
      setMensaje({ texto: 'Error al guardar. Intenta de nuevo.', tipo: 'err' });
    } finally {
      setGuardando(false);
      setTimeout(() => setMensaje(null), 3500);
    }
  };

  if (cargando) return <div className="card">Cargando impresoras...</div>;

  const opciones = [
    { nombre: '', nombreMostrar: '— Sin impresora —', esPredeterminada: false },
    ...impresoras,
  ];

  return (
    <div className="space-y-5 max-w-xl">
      {impresoras.length === 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          No se detectaron impresoras instaladas en Windows. Instala los drivers de tu impresora térmica y vuelve a esta pantalla.
        </div>
      )}

      {/* ── CAJA ── */}
      <div className="card space-y-3">
        <h3 className="font-semibold text-base">Impresora de Caja (tickets al cliente)</h3>
        <p className="text-sm text-slate-600">
          Se usa al cobrar. Siempre se genera también un PDF como respaldo histórico.
        </p>

        <div>
          <label className="label" htmlFor="impCaja">Impresora</label>
          <select id="impCaja" className="input" value={caja} onChange={(e) => setCaja(e.target.value)}>
            {opciones.map((imp) => (
              <option key={imp.nombre} value={imp.nombre}>
                {imp.nombreMostrar}{imp.esPredeterminada ? ' (predeterminada)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="impAncho">Ancho de papel</label>
          <select id="impAncho" className="input" value={ancho} onChange={(e) => setAncho(e.target.value)}>
            <option value="58">58 mm (compacta)</option>
            <option value="80">80 mm (estándar)</option>
          </select>
        </div>

        <div className={`rounded-lg border px-3 py-2 text-sm ${caja ? 'border-green-200 bg-green-50 text-green-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
          {caja ? `Imprimirá en: ${caja}` : 'Sin impresora → solo PDF al cobrar'}
        </div>
      </div>

      {/* ── COCINA ── */}
      <div className="card space-y-3">
        <h3 className="font-semibold text-base">Impresora de Cocina (comanda)</h3>
        <p className="text-sm text-slate-600">
          Imprime automáticamente al enviar un pedido a cocina. No incluye precios.
        </p>

        <div>
          <label className="label" htmlFor="impCocina">Impresora</label>
          <select id="impCocina" className="input" value={cocina} onChange={(e) => setCocina(e.target.value)}>
            {opciones.map((imp) => (
              <option key={imp.nombre} value={imp.nombre}>
                {imp.nombreMostrar}{imp.esPredeterminada ? ' (predeterminada)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Sprint 56.1 — Un solo equipo térmico en la caja es lo común en
            negocios chicos: la comanda sale por ahí en vez de perderse. */}
        {!cocina && (
          <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5"
              checked={cocinaUsarCaja}
              onChange={(e) => setCocinaUsarCaja(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">Imprimir la comanda en la impresora de Caja</span>
              <span className="block text-slate-600">
                Úsalo si sólo tienes una impresora. La cocina recibe su papel desde la caja.
              </span>
            </span>
          </label>
        )}

        <div className={`rounded-lg border px-3 py-2 text-sm ${cocina || cocinaUsarCaja ? 'border-green-200 bg-green-50 text-green-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
          {cocina
            ? `Imprimirá en: ${cocina}`
            : cocinaUsarCaja
              ? caja
                ? `La comanda saldrá en la impresora de Caja: ${caja}`
                : 'Elige primero una impresora de Caja arriba'
              : 'Sin impresora → cocina no recibe ticket automático'}
        </div>
      </div>

      {mensaje && (
        <div className={`rounded-lg px-4 py-2 text-sm ${mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {mensaje.texto}
        </div>
      )}

      <button onClick={guardar} disabled={guardando || cargando} className="btn-primary">
        {guardando ? 'Guardando...' : 'Guardar configuración'}
      </button>
    </div>
  );
}

/**
 * Sprint 56 — Pestaña Impresora en modo navegador.
 *
 * No se listan impresoras de Windows a propósito: el servidor corre en otra
 * máquina y nunca verá la térmica conectada a esta caja. Quien elige el
 * dispositivo es el diálogo de impresión del navegador.
 */
function ConfigImpresoraWebSection() {
  const [ancho, setAncho] = useState('80');
  const [autoTicket, setAutoTicket] = useState(true);
  const [autoComanda, setAutoComanda] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [mensaje, setMensaje] = useState<{ texto: string; tipo: 'ok' | 'err' } | null>(null);
  // Sprint 58 — Con agente conectado no hay diálogo de impresión que configurar.
  const [agenteConectado, setAgenteConectado] = useState(false);

  useEffect(() => {
    (async () => {
      const [anchoRes, ticketRes, comandaRes] = await Promise.all([
        window.api.config.obtener('impresora_ancho_mm'),
        window.api.config.obtener('web_auto_imprimir_ticket'),
        window.api.config.obtener('web_auto_imprimir_comanda'),
      ]);
      if (anchoRes.ok && anchoRes.data) setAncho(anchoRes.data);
      // Sin valor guardado el default es imprimir: es lo que espera una caja.
      if (ticketRes.ok) setAutoTicket(ticketRes.data !== 'false');
      if (comandaRes.ok) setAutoComanda(comandaRes.data !== 'false');
      setCargando(false);
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      await Promise.all([
        window.api.config.actualizar('impresora_ancho_mm', ancho),
        window.api.config.actualizar('web_auto_imprimir_ticket', autoTicket ? 'true' : 'false'),
        window.api.config.actualizar('web_auto_imprimir_comanda', autoComanda ? 'true' : 'false'),
      ]);
      setMensaje({ texto: 'Configuración guardada.', tipo: 'ok' });
    } catch {
      setMensaje({ texto: 'Error al guardar. Intenta de nuevo.', tipo: 'err' });
    } finally {
      setGuardando(false);
      setTimeout(() => setMensaje(null), 3500);
    }
  };

  const imprimirPrueba = async () => {
    setProbando(true);
    const res = await imprimirDocumento({ tipo: 'prueba' });
    setProbando(false);
    setMensaje(
      res.ok
        ? { texto: 'Se abrió el cuadro de impresión. Elige tu impresora térmica.', tipo: 'ok' }
        : { texto: res.error ?? 'No se pudo abrir el cuadro de impresión.', tipo: 'err' },
    );
    setTimeout(() => setMensaje(null), 5000);
  };

  if (cargando) return <div className="card">Cargando configuración...</div>;

  return (
    <div className="space-y-5 max-w-xl">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <p className="font-medium">Estás usando el sistema desde el navegador.</p>
        <p className="mt-1">
          El ticket se imprime desde esta computadora: al cobrar se abre el cuadro de
          impresión y ahí eliges tu impresora térmica. No hace falta configurarla aquí.
        </p>
      </div>

      <AgenteImpresionSection onConectado={setAgenteConectado} />

      {/*
        Sprint 58 — Estos tres ajustes viven en el cuadro de impresión de Chrome y
        ninguna página web puede tocarlos. Sin ellos el ticket sale con la
        dirección de internet y el número de página impresos, y con papel de más.
        Chrome los recuerda para la misma impresora: se hace una sola vez.

        Con el agente conectado esto deja de aplicar —no hay diálogo que
        configurar—, así que el aviso desaparece: instrucciones que ya no hacen
        falta sólo estorban a quien opera la caja.
      */}
      {!agenteConectado && (
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-medium">La primera vez, dentro del cuadro de impresión:</p>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Abre <span className="font-medium">Más configuraciones</span>.</li>
          <li>En <span className="font-medium">Márgenes</span> elige <span className="font-medium">Ninguno</span>.</li>
          <li>
            Desmarca <span className="font-medium">Encabezados y pies de página</span> (es lo
            que imprime la dirección de internet y el «1/1» al final del papel).
          </li>
        </ul>
        <p className="mt-1">Tu navegador recuerda estos ajustes para las siguientes impresiones.</p>
      </div>
      )}

      <div className="card space-y-3">
        <h3 className="font-semibold text-base">Papel</h3>
        <div>
          <label className="label" htmlFor="impAnchoWeb">Ancho de papel</label>
          <select
            id="impAnchoWeb"
            className="input"
            value={ancho}
            onChange={(e) => setAncho(e.target.value)}
          >
            <option value="58">58 mm (compacta)</option>
            <option value="80">80 mm (estándar)</option>
          </select>
        </div>
        <button
          onClick={imprimirPrueba}
          disabled={probando}
          className="btn-secondary flex items-center gap-2 min-h-[44px]"
        >
          <Printer className="h-5 w-5" />
          {probando ? 'Abriendo...' : 'Imprimir hoja de prueba'}
        </button>
        <p className="text-sm text-slate-600">
          Imprime una hoja con una regla. Si la regla se corta, el ancho elegido no
          coincide con el papel que tiene la impresora.
        </p>
      </div>

      <div className="card space-y-3">
        <h3 className="font-semibold text-base">Cuándo imprimir solo</h3>

        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={autoTicket}
            onChange={(e) => setAutoTicket(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium">Ticket del cliente al cobrar</span>
            <span className="block text-slate-600">
              Al terminar el cobro se abre el cuadro de impresión con el ticket.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={autoComanda}
            onChange={(e) => setAutoComanda(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium">Comanda de cocina al enviar el pedido</span>
            <span className="block text-slate-600">
              Misma idea, pero con la comanda sin precios.
            </span>
          </span>
        </label>

        <p className="text-sm text-slate-600">
          Si los apagas, el cobro sigue guardando el ticket en PDF y puedes imprimirlo
          después.
        </p>
      </div>

      {mensaje && (
        <div className={`rounded-lg px-4 py-2 text-sm ${mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {mensaje.texto}
        </div>
      )}

      <button onClick={guardar} disabled={guardando} className="btn-primary min-h-[44px]">
        {guardando ? 'Guardando...' : 'Guardar configuración'}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  /*
    Sprint 120 — La etiqueta ENVUELVE al campo, y así queda vinculada de verdad.

    Antes el rótulo era un `<div>` suelto encima del input: se ve igual, y para el navegador no
    tiene nada que ver con el campo. Consecuencias reales, no teóricas: un lector de pantalla anuncia
    "cuadro de texto, en blanco" sin decir de qué, y tocar el rótulo no lleva el foco al campo — que
    en una pantalla táctil es un blanco desperdiciado justo encima del que sí funciona.

    Se envuelve en `<label>` en vez de repartir `id`/`htmlFor`: la asociación es implícita, no hay
    identificadores que inventar ni que puedan repetirse, y no cambia una sola línea de quien lo usa.
  */
  return (
    <label className="block">
      <div className="label">{label}</div>
      {children}
    </label>
  );
}
